import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './FilterGroup.module.scss';
import Icon from '../icon/Icon';
import Button from '../button/Button';
import Modal from '../modal/Modal';
import Checkbox from '../checkbox/Checkbox';
import ProfilePicture from '../profilePicture/ProfilePicture';
import LiveSearch from '../liveSearch/LiveSearch';

const FilterModal = ({ categories, initialFilters, onClose, onApply }) => {
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id ?? '');
  const [searchQuery, setSearchQuery] = useState('');
  const [tempFilters, setTempFilters] = useState(() => {
    const init = {};
    categories.forEach(c => { init[c.id] = []; });
    return { ...init, ...initialFilters };
  });

  useEffect(() => {
    const init = {};
    categories.forEach(c => { init[c.id] = []; });
    setTempFilters({ ...init, ...initialFilters });
    setSearchQuery('');
  }, [initialFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (categoryId, itemId) => {
    setTempFilters(prev => {
      const current = prev[categoryId] || [];
      const next = current.includes(itemId)
        ? current.filter(id => id !== itemId)
        : [...current, itemId];
      return { ...prev, [categoryId]: next };
    });
  };

  const handleClearCategory = () => {
    setTempFilters(prev => ({ ...prev, [activeCategory]: [] }));
  };

  const handleClearAll = () => {
    const cleared = {};
    categories.forEach(c => { cleared[c.id] = []; });
    setTempFilters(cleared);
  };

  const currentCategory = categories.find(c => c.id === activeCategory);
  const filteredData = (currentCategory?.data || []).filter(item =>
    (currentCategory?.getLabel(item) ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal scrollable={false}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Filter</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="plus" className={styles.crossIcon} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.sidebar}>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`${styles.categoryTab} ${activeCategory === cat.id ? styles.active : ''}`}
                onClick={() => { setActiveCategory(cat.id); setSearchQuery(''); }}
              >
                {cat.label}
                {tempFilters[cat.id]?.length > 0 && (
                  <span className={styles.count}>{tempFilters[cat.id].length}</span>
                )}
              </button>
            ))}
          </div>

          <div className={styles.content}>
            <h3 className={styles.contentTitle}>{currentCategory?.label}</h3>
            <div className={styles.searchWrapper}>
              <LiveSearch
                placeholder={`Search ${currentCategory?.label}s`}
                onChange={setSearchQuery}
                value={searchQuery}
              />
            </div>
            <div className={styles.listWrapper}>
              {filteredData.map(item => {
                const isSelected = tempFilters[activeCategory]?.includes(item.id);
                return (
                  <div
                    key={item.id}
                    className={`${styles.listItem} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleToggle(activeCategory, item.id)}
                  >
                    <Checkbox checked={isSelected} onChange={() => handleToggle(activeCategory, item.id)} />
                    {currentCategory?.type === 'user' ? (
                      <div className={styles.userInfo}>
                        <ProfilePicture
                          src={item.profilePicture}
                          firstName={item.firstName}
                          lastName={item.lastName}
                          size="xs"
                        />
                        <span className={styles.name}>{currentCategory.getLabel(item)}</span>
                      </div>
                    ) : (
                      <span className={styles.name}>{currentCategory?.getLabel(item)}</span>
                    )}
                  </div>
                );
              })}
              {filteredData.length === 0 && (
                <div className={styles.emptyState}>No results found</div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.clearAllLink} onClick={handleClearAll}>Clear All</button>
          <div className={styles.footerRight}>
            <button className={styles.clearBtn} onClick={handleClearCategory}>Clear</button>
            <Button label="Apply" variant="primary" size="medium" onClick={() => onApply(tempFilters)} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

FilterModal.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    getLabel: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['user', 'default']),
  })).isRequired,
  initialFilters: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

const FilterGroup = ({ categories, filters, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const filterCount = Object.values(filters).reduce((acc, arr) => acc + (arr?.length || 0), 0);

  const handleClear = () => {
    const cleared = {};
    categories.forEach(c => { cleared[c.id] = []; });
    onChange(cleared);
  };

  return (
    <>
      <div className={styles.filterGroup}>
        <button className={styles.filterButton} onClick={() => setIsOpen(true)}>
          <Icon name="filter-slider" />
          <span>
            Filter{filterCount > 0 && <span className={styles.filterCount}> {filterCount}</span>}
          </span>
        </button>
        {filterCount > 0 && (
          <button className={styles.clearFilter} onClick={handleClear}>Clear</button>
        )}
      </div>

      {isOpen && (
        <FilterModal
          categories={categories}
          initialFilters={filters}
          onClose={() => setIsOpen(false)}
          onApply={(newFilters) => {
            onChange(newFilters);
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

FilterGroup.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    getLabel: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['user', 'default']),
  })).isRequired,
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default FilterGroup;
